import { useState, useMemo } from 'react';
import { BookOpen, Download, FileText } from 'lucide-react';
import PortalLayout from '../shared/components/PortalLayout';
import { useAuth } from '../context/AuthContext';
import { getManualsForUser } from './index';
import ManualContent from './ManualContent';
import { AMBER, AMBER_DARK, NAVY, BORDER_COLOR, bubbleStyle } from '../styles/tokens';

/** Print CSS: hide the portal chrome + in-app nav, render the full document. */
const PRINT_CSS = `
  .manual-print { display: none; }
  @media print {
    .sidebar-desktop, .sidebar-mobile-toggle, .sidebar-mobile-overlay { display: none !important; }
    .main-content { margin-left: 0 !important; }
    .manual-no-print { display: none !important; }
    .manual-screen { display: none !important; }
    .manual-print { display: block !important; }
    .manual-print h1, .manual-print h2, .manual-print h3 { break-after: avoid; }
    .manual-print section { break-inside: avoid; }
  }
`;

export default function ManualPage() {
  const { user } = useAuth();
  const manuals = useMemo(() => getManualsForUser(user), [user]);

  // Selected section identified by manual index + section id.
  const firstManual = manuals[0];
  const [selected, setSelected] = useState(
    firstManual ? { m: 0, s: firstManual.sections[0]?.id } : null
  );

  const activeManual = selected ? manuals[selected.m] : null;
  const activeSection = activeManual?.sections.find(sec => sec.id === selected.s) || null;
  const showRoleHeaders = manuals.length > 1;

  if (manuals.length === 0) {
    return (
      <PortalLayout activeModule="manual">
        <div style={{ padding: '28px 32px', maxWidth: 720, margin: '0 auto' }}>
          <div style={{ ...bubbleStyle, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            No hay manual disponible para su rol.
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeModule="manual">
      <style>{PRINT_CSS}</style>

      {/* ── Screen view: left section-nav + right content pane ── */}
      <div className="manual-screen" style={{ padding: '24px 28px', maxWidth: 1080, margin: '0 auto' }}>
        {/* Header */}
        <div className="manual-no-print" style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 22 }}>
          <BookOpen size={26} color={NAVY} style={{ marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
              Manual de Usuario
            </h1>
            <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
              Consulte aquí las instrucciones para su rol. Puede leerlo en pantalla o descargarlo en PDF.
            </p>
          </div>
          <button
            onClick={() => window.print()}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: AMBER, color: '#fff', border: 'none',
              borderRadius: 8, padding: '10px 18px',
              fontWeight: 600, fontSize: 13, cursor: 'pointer',
              fontFamily: 'Inter, sans-serif', flexShrink: 0, whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => e.currentTarget.style.background = AMBER_DARK}
            onMouseLeave={e => e.currentTarget.style.background = AMBER}
          >
            <Download size={15} /> Descargar PDF
          </button>
        </div>

        {/* Two-pane layout */}
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Left nav */}
          <nav
            className="manual-no-print"
            style={{
              width: 280, flexShrink: 0, position: 'sticky', top: 24,
              ...bubbleStyle, padding: '14px 12px', maxHeight: 'calc(100vh - 140px)', overflowY: 'auto',
            }}
          >
            {manuals.map((manual, mi) => (
              <div key={manual.roleKey} style={{ marginBottom: mi < manuals.length - 1 ? 12 : 0 }}>
                {showRoleHeaders && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                    color: NAVY, padding: '6px 10px 4px',
                  }}>
                    <span>{manual.icon}</span> {manual.roleLabel}
                  </div>
                )}
                {manual.sections.map(sec => {
                  const isActive = selected?.m === mi && selected?.s === sec.id;
                  return (
                    <button
                      key={sec.id}
                      onClick={() => setSelected({ m: mi, s: sec.id })}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '8px 12px', borderRadius: 8, marginBottom: 2,
                        fontSize: 13, lineHeight: 1.4,
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? AMBER : '#475569',
                        background: isActive ? '#fffbeb' : 'transparent',
                        border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                        borderLeft: isActive ? `2px solid ${AMBER}` : '2px solid transparent',
                      }}
                    >
                      {sec.title}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Right content pane */}
          <div style={{ flex: 1, minWidth: 0, ...bubbleStyle, padding: '26px 30px' }}>
            {activeSection && (
              <>
                {activeManual.sections.length > 0 && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                    {activeSection.group || activeManual.roleLabel}
                  </div>
                )}
                <h2 style={{ fontSize: 19, fontWeight: 700, color: NAVY, margin: '0 0 18px' }}>
                  {activeSection.title}
                </h2>
                <ManualContent blocks={activeSection.blocks} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Print view: full document (all visible manuals, every section) ── */}
      <div className="manual-print" style={{ padding: '0 8px', color: NAVY }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <FileText size={22} color={AMBER} />
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Manual de Usuario — V&A Portal</h1>
        </div>
        {manuals.map(manual => (
          <div key={manual.roleKey} style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, borderBottom: `2px solid ${BORDER_COLOR}`, paddingBottom: 4, margin: '18px 0 12px' }}>
              {manual.icon} {manual.roleLabel}
            </h2>
            {manual.sections.map(sec => (
              <section key={sec.id} style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>{sec.title}</h3>
                <ManualContent blocks={sec.blocks} />
              </section>
            ))}
          </div>
        ))}
      </div>
    </PortalLayout>
  );
}
